import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";

export interface PuduShopLiable {
  shopId: string;
  shopName: string | null;
  companyId: string | null;
  companyName: string | null;
  nbRobots: number;
}

export function usePuduShopsLiables(q: string, enabled: boolean) {
  return useQuery<{ items: PuduShopLiable[]; total: number }>({
    queryKey: ["/api/pudu-shops/liables", q],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "200" });
      if (q) params.set("q", q);
      const res = await authFetch(`/api/pudu-shops/liables?${params.toString()}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur lors de la recherche des magasins");
      }
      return res.json();
    },
    enabled,
  });
}

export function usePuduShopDetail(shopId: string | null | undefined) {
  return useQuery<PuduShopLiable>({
    queryKey: ["/api/pudu-shops", shopId],
    queryFn: async () => {
      const res = await authFetch(`/api/pudu-shops/${shopId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur lors de la lecture du magasin");
      }
      return res.json();
    },
    enabled: !!shopId,
  });
}

export function useLierPuduShop(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ shopId, replace }: { shopId: string; replace?: boolean }) => {
      const res = await authFetch(`/api/clients/${clientId}/pudu-shop/lier`, {
        method: "POST",
        body: JSON.stringify({ shopId, replace: !!replace }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err: any = new Error(data.message || "Erreur lors de la liaison au magasin Pudu");
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/clients"] });
      qc.invalidateQueries({ queryKey: ["/api/clients", clientId, "robots"] });
      qc.invalidateQueries({ queryKey: ["/api/pudu-shops/liables"] });
      qc.invalidateQueries({ queryKey: ["/api/pudu-shops"] });
    },
  });
}

export function useDelierPuduShop(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/clients/${clientId}/pudu-shop/delier`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Erreur lors de la déliaison du magasin Pudu");
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/clients"] });
      qc.invalidateQueries({ queryKey: ["/api/clients", clientId, "robots"] });
      qc.invalidateQueries({ queryKey: ["/api/pudu-shops/liables"] });
      qc.invalidateQueries({ queryKey: ["/api/pudu-shops"] });
    },
  });
}
